import { RouteComponentProps } from "../../../../models/RouteComponent";
import { CardActions, CardOverflow, Typography } from "@mui/joy";
import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";
import useApiService from "../../../../hooks/useApiService";
import { CompanyDocumentRecord } from "../../../../models/DSF/CompanyDocuments";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../../../utils/DateUtils";
import { StatusBadge } from "../../../../components/StatusBadge/StatusBadge";
import { downloadFile } from "../../../../service/s3Service";
import { Toast } from "primereact/toast";
import { Download, Eye, ProgressActivity } from "../../../../assets/icons";
import { useS3Service } from "../../../../hooks/useS3Service";

function CompanyDocuments(props: RouteComponentProps) {
  const { session } = props;
  const [downloading, setDownloading] = useState<string[]>([]);
  const [openingFile, setOpeningFile] = useState<string[]>([]);
  const { openFileInNewTab } = useS3Service();

  const toast = useRef<any>(null);
  // TODO add pagination when the API is ready
  const { data, loading, error } = useApiService<CompanyDocumentRecord[]>({
    method: "GET",
    url: "company-documents",
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading]);

  useEffect(() => {
    if (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Could not load company documents",
      });
    }
  }, [error]);

  const displayExpired = (data: CompanyDocumentRecord) => {
    // calculate the difference between the current date and the expire date
    // if the difference is less than 30 days, display the date in warning color
    // if the difference is past the expire date, display the date in error color
    // if the difference is more than 30 days, display the date in success color
    const currentDate = new Date();
    const expireDate = new Date(data.expire_at);
    const diffTime = expireDate.getTime() - currentDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    let variant: "error" | "success" | "info" | "warning" = "success";
    if (diffDays < 30) {
      variant = "warning";
    }
    if (diffDays < 0) {
      variant = "error";
    }
    if (data.expired) {
      variant = "error";
    }
    if (data.expired === null) {
      return <>{formatDate(data.expire_at, "short")}</>;
    }
    return <StatusBadge variant={variant}>{formatDate(data.expire_at, "short")}</StatusBadge>;
  };

  const displayUploadedOn = (data: CompanyDocumentRecord) => {
    return <>{formatDate(data.uploaded_on, "short")}</>;
  };

  const displayRequired = (data: CompanyDocumentRecord) => {
    return <>{data.required ? "Yes" : "No"}</>;
  };

  const displayDownloadButton = (data: CompanyDocumentRecord) => {
    if (!data.s3_key) return "—";

    return (
      <div className={"flex items-center gap-4"}>
        <div
          className={"cursor-pointer"}
          onClick={async () => {
            const downloadingCopy = [...downloading];
            downloadingCopy.push(data.s3_key);
            setDownloading(downloadingCopy);
            const down = await downloadFile(data.name, data.s3_key);
            const downloadingCopy2 = [...downloading];
            const index = downloadingCopy2.indexOf(data.s3_key);
            downloadingCopy2.splice(index, 1);
            setDownloading(downloadingCopy2);
            if (down) {
              toast.current.show({
                severity: "success",
                summary: "Downloaded",
                detail: `${data.name} downloaded successfully`,
              });
            } else {
              toast.current.show({
                severity: "error",
                summary: "Error",
                detail: `Could not download ${data.name}`,
              });
            }
          }}
        >
          {downloading.includes(data.s3_key) ? (
            <ProgressActivity className={"spinner"} />
          ) : (
            <Download className={"download-icon"} />
          )}
        </div>
        <div
          className={"cursor-pointer"}
          onClick={async () => {
            setOpeningFile([...openingFile, data.s3_key]);
            await openFileInNewTab(data.name, data.s3_key);
            const openingFileCopy = [...openingFile];
            const index = openingFileCopy.indexOf(data.s3_key);
            openingFileCopy.splice(index, 1);
            setOpeningFile(openingFileCopy);
          }}
        >
          {openingFile.includes(data.s3_key) ? <ProgressActivity className={"spinner"} /> : <Eye />}
        </div>
      </div>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Company documents
      </Typography>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions
          style={{ marginTop: 15, marginBottom: 15 }}
          sx={{ alignSelf: "flex-end", pt: 2 }}
        >
          <Button
            name="button"
            icon="pi pi-save"
            label="Download PDF"
            className="p-button-outlined"
            size="small"
            disabled={true}
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => {}}
          />
        </CardActions>
      </CardOverflow>
      {data && (
        <DataTable value={data} tableStyle={{ minWidth: "50rem" }}>
          <Column field={"name"} header={"Name"} sortable />
          <Column field={"description"} header={"Description"} sortable={false} />
          <Column field={"expire_at"} header={"Expire at"} body={displayExpired} sortable />
          <Column field={"updated_on"} header={"Updated On"} body={displayUploadedOn} sortable />
          <Column field={"required"} header={"Required"} body={displayRequired} sortable={false} />
          <Column
            field={"s3_key"}
            header={"Download"}
            body={displayDownloadButton}
            sortable={false}
          />
        </DataTable>
      )}
      <Toast ref={toast} />
    </>
  );
}

export default CompanyDocuments;
